import type { ButtonProps } from '~/components/ui/button';
import { ArchiveToggleButton } from '~/features/list-archive/components/archive-toggle-button';

import { useSetEnrichmentJobArchived } from '../hooks/use-set-enrichment-job-archived';

import { StopAndArchiveDialog } from './stop-and-archive-dialog';

interface EnrichmentJobArchiveControlProps {
  jobId: string;
  /** The row's current stamp — `archived` is derived from it. */
  archivedAt: string | null | undefined;
  buttonVariant?: ButtonProps['variant'];
  buttonSize?: ButtonProps['size'];
  showLabel?: boolean;
  className?: string;
  /** Forwarded to the toggle's `data-testid` (defaults to `archive-toggle-button`). */
  testId?: string;
}

/**
 * Archive / Unarchive for one enrichment job (Catalog Operations and Feed Audit
 * rows and detail headers): the toggle, its mutation, and the Stop / Reconcile
 * dialog a refused, still-executing run opens. Owns its own mutation instance so
 * table column definitions stay memoizable and the pending spinner is per row.
 */
export function EnrichmentJobArchiveControl({
  jobId,
  archivedAt,
  buttonVariant,
  buttonSize,
  showLabel,
  className,
  testId,
}: EnrichmentJobArchiveControlProps) {
  const { archive, unarchive, pendingJobId, refusal, dismissRefusal } =
    useSetEnrichmentJobArchived();
  return (
    <>
      <ArchiveToggleButton
        archived={archivedAt != null}
        isPending={pendingJobId === jobId}
        onArchive={() => archive(jobId)}
        onUnarchive={() => {
          unarchive(jobId);
        }}
        {...(buttonVariant !== undefined ? { buttonVariant } : {})}
        {...(buttonSize !== undefined ? { buttonSize } : {})}
        {...(showLabel !== undefined ? { showLabel } : {})}
        {...(className !== undefined ? { className } : {})}
        {...(testId !== undefined ? { testId } : {})}
      />
      <StopAndArchiveDialog
        testId="stop-and-archive-dialog"
        refusal={refusal}
        onClose={dismissRefusal}
        onArchive={archive}
      />
    </>
  );
}
